import { getErrMsg } from '../lib/errorMsg';
import { useState, useEffect, useRef } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Upload, Search, Users, FileText, Download, X, Clock, File as FileIcon } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const fmt = (n) => new Intl.NumberFormat('en-IN').format(Number(n) || 0);
const fmtSize = (bytes) => {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const FILE_ICONS = {
  'application/pdf': { color: 'text-red-500', bg: 'bg-red-50' },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { color: 'text-green-600', bg: 'bg-green-50' },
  'application/vnd.ms-excel': { color: 'text-green-600', bg: 'bg-green-50' },
  'image/': { color: 'text-blue-500', bg: 'bg-blue-50' },
};

function getFileStyle(contentType) {
  if (!contentType) return { color: 'text-slate-500', bg: 'bg-slate-50' };
  for (const [key, style] of Object.entries(FILE_ICONS)) {
    if (contentType.startsWith(key) || contentType === key) return style;
  }
  return { color: 'text-slate-500', bg: 'bg-slate-50' };
}

function CustomerDocumentsModal({ customer, onClose }) {
  const { user } = useAuth();
  const canEdit = user?.role !== 'viewer';
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [parseResult, setParseResult] = useState(null);
  const fileRef = useRef(null);

  const fetchDocs = async () => {
    try {
      const res = await api.get(`/customers/${customer.customer_id}/documents`);
      setDocs(res.data);
    } catch { toast.error('Failed to load documents'); }
    setLoading(false);
  };

  useEffect(() => { fetchDocs(); }, [customer.customer_id]);

  const handleUpload = async () => {
    if (!selectedFile) { toast.error('Select a file first'); return; }
    if (selectedFile.size > 10 * 1024 * 1024) { toast.error('File exceeds 10 MB limit'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('label', label.trim() || selectedFile.name);
      formData.append('description', description.trim());
      const res = await api.post(`/customers/${customer.customer_id}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Document uploaded');
      setSelectedFile(null);
      setLabel('');
      setDescription('');
      if (fileRef.current) fileRef.current.value = '';
      // Show auto-parse results if any
      if (res.data?.auto_parse?.parts_updated?.length > 0) {
        setParseResult(res.data.auto_parse);
      }
      fetchDocs();
    } catch (err) {
      toast.error(getErrMsg(err));
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await api.get(`/documents/${doc.doc_id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.label}"?`)) return;
    try {
      await api.delete(`/documents/${doc.doc_id}`);
      toast.success('Document deleted');
      fetchDocs();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-slate-900">
            Documents — <span className="text-orange-500">{customer.name}</span>
          </DialogTitle>
          <p className="text-xs text-slate-500 mt-0.5">Upload and manage costing files, RFQs, and other documents</p>
        </DialogHeader>

        {/* Upload Section */}
        {canEdit && (
          <div className="bg-slate-50 border border-dashed border-slate-300 rounded-sm p-4 mt-2">
            <div className="flex items-center gap-2 mb-3">
              <Upload size={14} className="text-slate-500" />
              <span className="text-xs font-bold uppercase text-slate-500">Upload New Document</span>
            </div>
            <div className="space-y-2">
              <div className="flex gap-2">
                <input
                  ref={fileRef}
                  type="file"
                  onChange={e => setSelectedFile(e.target.files?.[0] || null)}
                  data-testid="doc-file-input"
                  className="flex-1 text-xs file:mr-2 file:py-1.5 file:px-3 file:rounded-sm file:border-0 file:text-xs file:font-semibold file:bg-slate-900 file:text-white hover:file:bg-slate-800 file:cursor-pointer"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="text" value={label} onChange={e => setLabel(e.target.value)}
                  placeholder="Label (e.g., Q1 2026 Costing)"
                  data-testid="doc-label-input"
                  className="border border-slate-300 rounded-sm h-8 px-3 text-xs focus:outline-none focus:ring-2 focus:ring-slate-900"
                />
                <input
                  type="text" value={description} onChange={e => setDescription(e.target.value)}
                  placeholder="Description (optional)"
                  data-testid="doc-description-input"
                  className="border border-slate-300 rounded-sm h-8 px-3 text-xs focus:outline-none focus:ring-2 focus:ring-slate-900"
                />
              </div>
              {selectedFile && (
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-slate-400">
                    {selectedFile.name} ({fmtSize(selectedFile.size)})
                  </span>
                  <button onClick={handleUpload} disabled={uploading} data-testid="upload-doc-btn"
                    className="px-4 py-1.5 bg-slate-900 text-white text-xs font-bold uppercase rounded-sm hover:bg-slate-800 disabled:opacity-50 transition-colors">
                    {uploading ? 'Uploading...' : 'Upload'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Auto-parse Results */}
        {parseResult && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4 mt-2" data-testid="auto-parse-result">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <FileText size={14} className="text-emerald-600" />
                <span className="text-xs font-bold uppercase text-emerald-700">Costing Data Auto-Extracted</span>
              </div>
              <button onClick={() => setParseResult(null)} className="text-slate-400 hover:text-slate-600">
                <X size={14} />
              </button>
            </div>
            <p className="text-xs text-emerald-600 mb-2">
              {parseResult.new_entries} new quarterly entries added to {parseResult.parts_updated.length} part(s)
            </p>
            {parseResult.parts_updated.map(p => (
              <div key={p.part_number} className="flex items-center justify-between py-1.5 border-t border-emerald-100">
                <div>
                  <span className="text-xs font-semibold text-slate-800">{p.part_number}</span>
                  <span className="text-[10px] text-slate-400 ml-2">{p.part_name}</span>
                  <span className="text-[10px] text-emerald-600 ml-2">+{p.new_entries} entries</span>
                </div>
                <div className="text-right">
                  <span className="text-xs font-mono font-bold text-emerald-700">G.Total: {p.latest_grand_total?.toFixed(2)}</span>
                  <span className="text-[10px] text-slate-400 ml-2">Locked RM: {p.latest_rm?.toFixed(2)}/kg</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Documents List */}
        <div className="mt-3">
          <p className="text-xs font-bold uppercase text-slate-500 mb-2">
            {docs.length} Document{docs.length !== 1 ? 's' : ''}
          </p>
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3].map(i => <div key={i} className="h-14 bg-slate-100 rounded-sm animate-pulse" />)}
            </div>
          ) : docs.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm">No documents uploaded yet</div>
          ) : (
            <div className="space-y-1.5" data-testid="documents-list">
              {docs.map((doc) => {
                const style = getFileStyle(doc.content_type);
                return (
                  <div key={doc.doc_id}
                    data-testid={`doc-item-${doc.doc_id}`}
                    className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-sm hover:border-slate-300 group transition-colors">
                    <div className={`w-9 h-9 ${style.bg} rounded-sm flex items-center justify-center flex-shrink-0`}>
                      <FileText size={16} className={style.color} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-slate-800 truncate">{doc.label}</p>
                      <div className="flex items-center gap-2 text-[10px] text-slate-400">
                        <span>{doc.original_filename}</span>
                        <span>&middot;</span>
                        <span>{fmtSize(doc.size)}</span>
                        <span>&middot;</span>
                        <span>{fmtDate(doc.uploaded_at)}</span>
                        {doc.uploaded_by && (
                          <>
                            <span>&middot;</span>
                            <span>{doc.uploaded_by}</span>
                          </>
                        )}
                      </div>
                      {doc.description && (
                        <p className="text-[10px] text-slate-400 mt-0.5 truncate">{doc.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                      <button onClick={() => handleDownload(doc)} title="Download"
                        data-testid={`download-doc-${doc.doc_id}`}
                        className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-sm">
                        <Download size={14} />
                      </button>
                      {canEdit && (
                        <button onClick={() => handleDelete(doc)} title="Delete"
                          data-testid={`delete-doc-${doc.doc_id}`}
                          className="p-1.5 text-red-400 hover:bg-red-50 rounded-sm">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CustomerModal({ customer, onClose, onSaved }) {
  const isEdit = !!customer;
  const [form, setForm] = useState({
    name: customer?.name || '',
    short_code: customer?.short_code || '',
    contact_person: customer?.contact_person || '',
    location: customer?.location || '',
    notes: customer?.notes || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Customer name is required'); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/customers/${customer.customer_id}`, form);
      } else {
        await api.post('/customers', form);
      }
      toast.success(isEdit ? 'Customer updated' : 'Customer added');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(getErrMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-heading text-slate-900">{isEdit ? 'Edit Customer' : 'Add Customer'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Customer Name *</label>
            <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              data-testid="customer-name-input" placeholder="e.g. Marelli"
              className="w-full border border-slate-300 rounded-sm h-9 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Short Code</label>
              <input type="text" value={form.short_code} onChange={e => setForm(f => ({ ...f, short_code: e.target.value }))}
                data-testid="customer-code-input" placeholder="e.g. MRL"
                className="w-full border border-slate-300 rounded-sm h-9 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Location</label>
              <input type="text" value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
                data-testid="customer-location-input" placeholder="e.g. Pune"
                className="w-full border border-slate-300 rounded-sm h-9 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Contact Person</label>
            <input type="text" value={form.contact_person} onChange={e => setForm(f => ({ ...f, contact_person: e.target.value }))}
              data-testid="customer-contact-input"
              className="w-full border border-slate-300 rounded-sm h-9 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Notes</label>
            <input type="text" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              data-testid="customer-notes-input"
              className="w-full border border-slate-300 rounded-sm h-9 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
          </div>
          <div className="flex gap-3 pt-1">
            <button onClick={onClose} className="flex-1 h-9 border border-slate-300 rounded-sm text-slate-700 text-sm font-semibold hover:bg-slate-50">Cancel</button>
            <button onClick={handleSave} disabled={saving} data-testid="save-customer-btn"
              className="flex-1 h-9 bg-slate-900 text-white text-sm font-bold uppercase rounded-sm hover:bg-slate-800 disabled:opacity-50 transition-colors">
              {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Add Customer'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Customers() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modalCustomer, setModalCustomer] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [docsCustomer, setDocsCustomer] = useState(null);

  const canEdit = user?.role !== 'viewer';

  const fetchCustomers = async () => {
    try {
      const res = await api.get('/customers');
      setCustomers(res.data);
    } catch { toast.error('Failed to load customers'); }
    setLoading(false);
  };

  useEffect(() => { fetchCustomers(); }, []);

  const filtered = customers.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    (c.short_code || '').toLowerCase().includes(search.toLowerCase())
  );

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete customer "${c.name}"? This won't delete their parts.`)) return;
    try {
      await api.delete(`/customers/${c.customer_id}`);
      toast.success('Customer deleted');
      fetchCustomers();
    } catch { toast.error('Failed to delete'); }
  };

  const totalParts = customers.reduce((s, c) => s + (c.part_count || 0), 0);

  return (
    <div className="space-y-5" data-testid="customers-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading font-bold text-slate-900 text-xl">Customer Master</h2>
          <p className="text-slate-500 text-sm mt-0.5">{customers.length} customers &middot; {totalParts} total parts</p>
        </div>
        {canEdit && (
          <button onClick={() => setShowAdd(true)} data-testid="add-customer-btn"
            className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white text-sm font-bold uppercase tracking-wide rounded-sm hover:bg-slate-800 shadow-sm transition-colors">
            <Plus size={15} /> Add Customer
          </button>
        )}
      </div>

      {/* Search */}
      <div className="relative max-w-xs">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
          data-testid="customer-search"
          placeholder="Search customers..."
          className="w-full pl-9 pr-3 h-9 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
      </div>

      {/* Customer grid */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-28 bg-slate-200 rounded-sm animate-pulse" />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {filtered.map(c => (
            <div key={c.customer_id}
              data-testid={`customer-card-${c.customer_id}`}
              className="bg-white border border-slate-200 rounded-sm shadow-sm p-4 group hover:border-slate-300 transition-colors">
              <div className="flex items-start justify-between mb-2">
                <div className="min-w-0 flex-1">
                  <h3 className="font-heading font-bold text-slate-900 text-sm truncate">{c.name}</h3>
                  {c.short_code && <p className="text-xs font-mono text-slate-400">{c.short_code}</p>}
                </div>
                <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  {canEdit && (
                    <>
                      <button onClick={() => setModalCustomer(c)} title="Edit"
                        data-testid={`edit-customer-${c.customer_id}`}
                        className="p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-sm">
                        <Edit size={12} />
                      </button>
                      <button onClick={() => handleDelete(c)} title="Delete"
                        data-testid={`delete-customer-${c.customer_id}`}
                        className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-sm">
                        <Trash2 size={12} />
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between mt-3">
                <div className="flex items-center gap-1.5">
                  <Users size={12} className="text-slate-400" />
                  <span className="text-xs text-slate-500">
                    {c.part_count || 0} part{c.part_count !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setDocsCustomer(c)}
                    data-testid={`view-docs-${c.customer_id}`}
                    className="flex items-center gap-1 text-[10px] font-semibold text-slate-400 hover:text-slate-700">
                    <FileText size={10} /> Docs
                  </button>
                  {c.part_count > 0 ? (
                    <button onClick={() => navigate(`/parts?customer=${encodeURIComponent(c.name)}`)}
                      className="text-[10px] font-semibold text-orange-600 hover:text-orange-700">
                      View Parts
                    </button>
                  ) : (
                    <span className="text-[10px] font-semibold text-slate-300">No parts yet</span>
                  )}
                </div>
              </div>
              {(c.location || c.contact_person) && (
                <div className="mt-2 pt-2 border-t border-slate-100 text-xs text-slate-400 truncate">
                  {c.location && <span>{c.location}</span>}
                  {c.location && c.contact_person && <span> &middot; </span>}
                  {c.contact_person && <span>{c.contact_person}</span>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="text-center py-12 text-slate-400">No customers found</div>
      )}

      {showAdd && <CustomerModal onClose={() => setShowAdd(false)} onSaved={fetchCustomers} />}
      {modalCustomer && <CustomerModal customer={modalCustomer} onClose={() => setModalCustomer(null)} onSaved={fetchCustomers} />}
      {docsCustomer && <CustomerDocumentsModal customer={docsCustomer} onClose={() => setDocsCustomer(null)} />}
    </div>
  );
}
